<template>
    <div class="book-insight-hero">
        <authentication-forms-modal @add-to-library="addToLibrary" />
        <bi-background
            :color="backgroundColor"
            :shape="backgroundShape"
        >
            <template #content>
                <div v-if="isFeatured" class="featured-bi">
                    <img
                        class="star"
                        src="@assets/img/book-insights/star.svg"
                        width="18px"
                        alt="Star"
                    >
                    <img class="img-fluid" src="@assets/img/book-insights/ribbon.png">
                </div>
                <category-pill>
                    <template #category-name>
                        Book Insight
                    </template>
                </category-pill>
                <h1 class="title">
                    {{ title }}
                </h1>
                <p class="author">
                    <span>
                        {{ authorsNames.join(", ") }}
                    </span>
                </p>
                <div class="cta">
                    <router-link
                        v-if="$route.name == 'bi-discover'"
                        :to="{ name: 'book-insight', params: { 'slug': bookInsightSlug }}"
                        class="btn btn-secondary btn-secondary-inverted"
                    >
                        About This Book Insight
                    </router-link>
                    <button
                        v-else-if="isBiLocked"
                        type="button"
                        class="btn btn-secondary btn-secondary-inverted"
                        @click="$modal.show('start-membership')"
                    >
                        Unlock
                    </button>
                    <button
                        v-else
                        class="btn btn-secondary btn-secondary-inverted"
                        @click="onReaderModeClicked"
                    >
                        Read or Listen
                    </button>
                    <!-- <button
                        type="button"
                        class="btn btn-outline-white"
                        :class="{ 'deactivated': isWorkingWithLibrary }"
                        @click.stop="toggleAddToLibrary"
                    >
                        {{ addToLibraryLabel }}
                    </button> -->
                </div>
            </template>
        </bi-background>
    </div>
</template>

<script>

import { mapGetters, mapState } from "vuex";
import bookInsightLibraryMixins from "@/mixins/bookInsightLibraryMixins.js";

export default {
    name: "BookInsightHero",
    components: {
        categoryPill: () => import(/* webpackChunkName: "category-pill"*/ "@c/books-insights/category-pill"),
        biBackground: () => import(/* webpackChunkName: "bi-background"*/ "@c/books-insights/background"),
        authenticationFormsModal: () => import(/* webpackChunkName: "authentication-forms"*/ "@c/modals/authentication-forms")
    },
    mixins: [bookInsightLibraryMixins],
    props: {
        bookInsight: {
            type: Object,
            required: true
        },
        title: {
            type: String,
            required: true
        },
        authors: {
            type: Array,
            default() {
                return [];
            }
        },
        isFeatured: {
            type: Boolean,
            default: false
        },
        bookInsightSlug: {
            type: String,
            default: ""
        },
        backgroundColor: {
            type: String,
            required: true
        },
        backgroundShape: {
            type: String,
            required: true
        }
    },
    computed: {
        ...mapGetters({
            nonFictionSubscriptionDaysLeft: "UserMembership/getNonFictionSubscriptionDaysLeft",
            isLoggedIn: "User/isLoggedIn"
        }),
        authorsNames() {
            if (!this.authors.length) {
                return [];
            }
            return this.authors.map((author) => author.name);
        },
        ...mapState({
            accessTypeIds: state => state.AccessTypes.idList
        }),
        addToLibraryLabel() {
            return this.$_hasBookInsightInLibrary ? "Remove from My Library" : "Add to My Library";
        },
        isBiLocked() {
            return this.nonFictionSubscriptionDaysLeft <= 0 && this.bookInsight.access_type == this.accessTypeIds.PREMIUM;
        }
    },
    methods: {
        onReaderModeClicked() {
            this.$emit("read-button-clicked");
        },
        toggleAddToLibrary() {
            if (!this.isLoggedIn) {
                this.$modal.show("authentication-forms-modal", { action: "add-to-library", formType: "sign-up" });
                return;
            }

            this.$_toggleAddToLibrary();
            const action = this.$_hasBookInsightInLibrary ? "remove" : "add";
            this.trackGTMEvent(action);
        },
        addToLibrary() {
            if (this.$_hasBookInsightInLibrary) {
                return;
            }
            this.$_toggleAddToLibrary();
        },
        trackGTMEvent(action) {
            const customEvents = {
                remove: {
                    event: "bi-nav-library-remove",
                    action: "remove"
                },
                add: {
                    event: "bi-nav-bookpage-addtolibrary",
                    action: "addtolibrary"
                }
            };

            const gtmEvent = {
                ...customEvents[action]
            }

            if (this.bookInsight.hasOwnProperty("slug")) {
                gtmEvent.insightSlug = this.bookInsight.slug;
            }

            if (this.bookInsight.hasOwnProperty("genre")) {
                gtmEvent.insightGenre = this.bookInsight.genre.name;
            }

            this.$_gtmTrackEvent(gtmEvent);
        }
    }
}
</script>

<style lang="scss">
.book-insight-hero {
    height: 430px;
    border-radius: 20px;
    overflow: hidden;
    position: relative;

    @media (max-width: $md) {
        height: 385px;

        .author {
            margin-bottom: 70px;
        }
    }

    > div {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    .book-insight-background {
        background-size: 35%;

        @media (max-width: $md) {
            padding-top: 60px;
        }
    }

    .featured-bi {
        width: 30px;
        position: absolute;
        left: 20px;
        top: 0;

        .star {
            position: absolute;
            left: 50%;
            transform: translate(-50%);
        }
    }

    .title {
        color: #FFFFFF;
        font-size: 40px;
        font-weight: 600;
        line-height: 1.3;
        margin-top: 25px;
        margin-bottom: 0;
        text-align: center;

        @media (max-width: $md) {
            font-size: 23px;
            line-height: 32px;
            margin-top: 20px;
        }
    }

    .author {
        color: #FFFFFF;
        font-size: 23px;
        line-height: 32px;
        font-weight: 300;
        margin-bottom: 60px;

        @media (max-width: $md) {
            font-size: 16px;
            line-height: 22px;
            margin-bottom: 25px;
        }
    }

    .cta {
        display: flex;
        flex-direction: column;

        @media (max-width: $md) {
            margin-top: auto;
            margin-bottom: 20px;
        }

        .btn-secondary {
            color: $primary-color !important;

            &:not(:disabled):not(.disabled):active, &:hover, &.hover {
                color: $primary-color !important;
            }
        }

        .btn-outline-white {
            &:hover, &.hover {
                color: $primary-color !important;
            }
        }

        .btn {
            width: 300px;

            @media (max-width: $md) {
                width: 260px;
            }

            &:first-child {
                margin-bottom: 10px;
            }
        }
    }
}
</style>

